import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchCartPrices } from "@store/slices/cartSlice/actions/fetchCartPrices";

import { getConfig } from "@jti/fe-common";
import { magento } from "@jti/magento";
import { getUser, LoyaltyApi } from "@jti/rest-api";
import { type CampaignOrderData, type GetEligibleCampaignsResponseType } from "@jti/rest-api-types";

import type { Loyalty } from "./loyaltySlice.types";

export const fetchLoyaltyStatus = createAsyncThunk("loyalty/status", async (_, { dispatch }) => {
  const config = getConfig();
  const user = getUser();
  const loyaltyApi = LoyaltyApi.init();

  if (config.IS_LOYALTY_ENABLED !== "true") {
    return {
      status: false,
      points: 0,
      pending_points: 0,
      vouchers: [],
    };
  }

  const status = await loyaltyApi.getStatus();
  if (status && user?.uuid && user?.ciam_token) {
    dispatch(fetchLoyaltyPoints());
  }

  return {
    status,
  };
});

export const fetchLoyaltyVouchers = createAsyncThunk<
  {
    vouchers: GetEligibleCampaignsResponseType[];
  },
  CampaignOrderData
>("loyalty/vouchers", async (data?: CampaignOrderData) => {
  const loyaltyApi = LoyaltyApi.init();
  const user = getUser();

  if (user?.uuid && user?.ciam_token) {
    const vouchers: GetEligibleCampaignsResponseType[] =
      (await loyaltyApi.getEligibleCampaigns(user.uuid, user.ciam_token, data)) || [];
    return { vouchers };
  }

  return { vouchers: [] };
});

const fetchLoyaltyPoints = createAsyncThunk("loyalty/fetchLoyaltyPoints", async (useLocalCache?: boolean) => {
  const user = getUser();
  const loyaltyApi = LoyaltyApi.init();

  let points = 0;
  let pending_points = 0;

  if (user?.uuid && user?.ciam_token) {
    const getPoints = await loyaltyApi.getPoints(user.uuid || "", user.ciam_token, useLocalCache);
    points = getPoints?.points || 0;
    pending_points = getPoints?.pending_points || 0;
  }

  return { points, pending_points };
});

export const applyCampaign = createAsyncThunk(
  "loyalty/applyCampaign",
  async ({ cartId, campaign_uuid }: { cartId: string; campaign_uuid: string }, { dispatch }) => {
    try {
      const response = await magento.applyCampaignToCart(cartId, campaign_uuid);
      dispatch(loyaltySlice.actions.setLoyaltyCampaignId(campaign_uuid));
      dispatch(fetchCartPrices(cartId));
      return response;
    } catch (error: any) {
      await dispatch(fetchLoyaltyPoints(false));
      dispatch(loyaltySlice.actions.setCampaignError(error.response.errors));
      await dispatch(removeCampaign(cartId));
      dispatch(loyaltySlice.actions.setCampaignError([]));
    }
  }
);

export const removeCampaign = createAsyncThunk("loyalty/removeCampaign", async (cartId: string, { dispatch }) => {
  await magento.removeCampaignFromCart(cartId);
  dispatch(fetchCartPrices(cartId));
  dispatch(loyaltySlice.actions.setLoyaltyCampaignId(""));
});

export const initialLoyalty: Loyalty = {
  points: 0,
  pending_points: 0,
  status: false,
  loyaltyChoice: undefined,
  loyaltyCampaignId: "",
  isLoyaltyError: false,
  vouchers: [],
  isLoaded: false,
  campaignErrors: [],
};

export const loyaltySlice = createSlice({
  name: "loyalty",
  initialState: initialLoyalty,
  reducers: {
    setLoyaltyChoice(state, action: PayloadAction<0 | 1>) {
      state.isLoyaltyError = false;
      state.loyaltyChoice = action.payload;
    },
    setLoyaltyCampaignId(state, action: PayloadAction<string>) {
      state.loyaltyCampaignId = action.payload;
    },
    setLoyaltyError(state, action: PayloadAction<boolean>) {
      state.isLoyaltyError = action.payload;
    },
    setCampaignError(state, action: PayloadAction<[]>) {
      state.campaignErrors = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchLoyaltyStatus.fulfilled,
      (state: Partial<Loyalty>, action: PayloadAction<Partial<Loyalty>>) => {
        state.status = action.payload.status;
        state.isLoaded = true;
      }
    );

    builder.addCase(
      fetchLoyaltyPoints.fulfilled,
      (state: Partial<Loyalty>, action: PayloadAction<Partial<Loyalty>>) => {
        state.points = action.payload.points;
        state.pending_points = action.payload.pending_points;
      }
    );

    builder.addCase(
      fetchLoyaltyVouchers.fulfilled,
      (state: Partial<Loyalty>, action: PayloadAction<Pick<Loyalty, "vouchers">>) => {
        state.vouchers = action.payload.vouchers;
      }
    );
  },
});
